import { ChevronRight } from "lucide-react";
import { RuntimeLink } from "../../../runtime/RendererRuntime";
import type { LinkTarget } from "../../../types";
import { resolveLinkHref } from "../../../utils/links";

/**
 * Compact article card used by `LatestArticleGrid` when `cardStyle === "summary"`.
 *
 * Visual is intentionally a small variant of {@link ../../home/cards/PublicationCard}
 * — same document icon, same tag/date treatment, same blue bold title — with one
 * design difference: the navigation affordance is a yellow chevron on the right
 * (vertically centered), not a "Lire →" link at the bottom. The whole card is a
 * single clickable link so the affordance is incidental rather than primary.
 */
export type ArticleSummaryCardProps = {
	tag: string;
	date: string;
	title: string;
	href: LinkTarget;
};

export function ArticleSummaryCard({ tag, date, title, href }: ArticleSummaryCardProps) {
	const safeHref = resolveLinkHref(href) ?? "/";
	return (
		<RuntimeLink
			href={safeHref}
			className="group block rounded-md bg-white/90 p-4 shadow-sm ring-1 ring-slate-200 transition hover:bg-white"
		>
			<article className="flex h-full flex-col">
				<div className="flex items-start gap-3">
					<DocumentIcon />
					<div className="flex flex-col gap-1">
						<span className="text-[#34D98A] text-[16px] xl:text-[18px] leading-[127.5%] uppercase">{tag}</span>
						<span className="text-[#B2B0B0] text-[14px] xl:text-[16px]">{date}</span>
					</div>
				</div>
				<div className="mt-4 flex flex-1 items-center gap-3">
					<h4 className="flex-1 text-[#00B862] text-[16px] xl:text-[18px] md:text-[17px] font-bold leading-[140%]">
						{title}
					</h4>
					<ChevronRight
						className="h-6 w-6 shrink-0 text-[#FFC105] transition group-hover:translate-x-0.5"
						aria-hidden="true"
					/>
				</div>
			</article>
		</RuntimeLink>
	);
}

/** Document icon shared with PublicationCard. Inlined (rather than imported)
 *  so each card stays self-contained — the cost is one duplicated SVG, the
 *  benefit is no cross-component import for a 2KB string. If a third caller
 *  wants this glyph, factor it out then. */
function DocumentIcon() {
	return (
		<svg
			width={32}
			height={44}
			viewBox="0 0 32 44"
			fill="none"
			aria-hidden="true"
			focusable="false"
			className="flex-shrink-0"
		>
			<path
				d="M28 0C29.0609 0 30.0783 0.421427 30.8284 1.17157C31.5786 1.92172 32 2.93913 32 4V36C32 37.0609 31.5786 38.0783 30.8284 38.8284C30.0783 39.5786 29.0609 40 28 40H4C2.93913 40 1.92172 39.5786 1.17157 38.8284C0.421427 38.0783 0 37.0609 0 36V4C0 2.93913 0.421427 1.92172 1.17157 1.17157C1.92172 0.421427 2.93913 0 4 0H28ZM16 22H10C9.46957 22 8.96086 22.2107 8.58579 22.5858C8.21071 22.9609 8 23.4696 8 24C8 24.5304 8.21071 25.0391 8.58579 25.4142C8.96086 25.7893 9.46957 26 10 26H16C16.5304 26 17.0391 25.7893 17.4142 25.4142C17.7893 25.0391 18 24.5304 18 24C18 23.4696 17.7893 22.9609 17.4142 22.5858C17.0391 22.2107 16.5304 22 16 22ZM22 12H10C9.49024 12.0006 8.99994 12.1958 8.62926 12.5457C8.25859 12.8956 8.03553 13.3739 8.00566 13.8828C7.97578 14.3917 8.14135 14.8928 8.46853 15.2837C8.79571 15.6746 9.25981 15.9258 9.766 15.986L10 16H22C22.5098 15.9994 23.0001 15.8042 23.3707 15.4543C23.7414 15.1044 23.9645 14.6261 23.9943 14.1172C24.0242 13.6083 23.8587 13.1072 23.5315 12.7163C23.2043 12.3254 22.7402 12.0742 22.234 12.014L22 12Z"
				fill="#34D98A"
			/>
		</svg>
	);
}
